import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  INVOICE_SCHEDULES,
  INVOICE_SCHEDULE_LABELS,
  canOpenInvoice,
  formatDate,
  formatDateTime,
  type CustomerInvoiceScheduleState,
  type InvoiceSchedule,
  type InvoiceScheduleRun,
  type UpdateInvoiceScheduleRequest,
} from '@codepass/shared';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/auth/AuthProvider';
import {
  Alert,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Label,
  Select,
  Skeleton,
} from '@/components/ui';
import { PermittedLink } from '@/components/domain';

/**
 * Kortet 'Fakturering' på /kunder/:id: hvornår kunden får sin faktura.
 *
 * Manuelt er standarden — controlleren laver fakturaen på /fakturering/ny. Med en plan laver
 * Codepass en kladde i Business Central dagen efter hver periode, med det samme grundlag og den
 * samme buffer som /fakturering/ny. Kladden ligger på /fakturering, til en controller har
 * godkendt den.
 */

const RUN_OUTCOME_TEXT: Record<InvoiceScheduleRun['outcome'], string> = {
  created: 'Kladde oprettet',
  nothing: 'Intet at fakturere',
  skipped: 'Ikke oprettet',
  failed: 'Fejlede',
};

const SCHEDULE_HINT: Record<InvoiceSchedule, string> = {
  manual: 'Fakturaen laves på /fakturering/ny.',
  week: 'En kladde hver mandag for ugen før, mandag til søndag.',
  half_month: 'En kladde den 1. og den 16. for halvmåneden før.',
  month: 'En kladde den 1. for måneden før.',
};

export function InvoiceScheduleCard({ customerKey }: { customerKey: string }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const queryKey = ['customer', customerKey, 'invoice-schedule'];

  const { data: state, isLoading } = useQuery({
    queryKey,
    queryFn: () =>
      api.get<CustomerInvoiceScheduleState>(
        `/customers/${encodeURIComponent(customerKey)}/invoice-schedule`,
      ),
    enabled: Boolean(customerKey),
  });

  /* Optimistisk: valget står med det samme og rulles tilbage, hvis det ikke kunne gemmes. */
  const save = useMutation({
    mutationFn: (body: UpdateInvoiceScheduleRequest) =>
      api.put<CustomerInvoiceScheduleState>(
        `/customers/${encodeURIComponent(customerKey)}/invoice-schedule`,
        body,
      ),
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<CustomerInvoiceScheduleState>(queryKey);
      if (previous) {
        queryClient.setQueryData<CustomerInvoiceScheduleState>(queryKey, {
          ...previous,
          schedule: body.schedule,
          next: body.schedule === 'manual' ? null : previous.next,
        });
      }
      return { previous };
    },
    onError: (cause, _body, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
      setError(cause instanceof ApiError ? cause.message : 'Kunne ikke gemme faktureringen.');
    },
    onSuccess: (result) => {
      setError(null);
      queryClient.setQueryData(queryKey, result);
    },
  });

  const canEdit = can('invoicing.settings');

  return (
    <Card>
      <CardHeader>
        <CardTitle>Fakturering</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3 text-sm">
        {isLoading || !state ? (
          <Skeleton className="h-16" />
        ) : (
          <>
            <div>
              <Label htmlFor="invoice-schedule">Kunden får sin faktura</Label>
              <Select
                id="invoice-schedule"
                value={state.schedule}
                disabled={!canEdit || save.isPending}
                onChange={(event) =>
                  save.mutate({ schedule: event.target.value as InvoiceSchedule })
                }
              >
                {INVOICE_SCHEDULES.map((schedule) => (
                  <option key={schedule} value={schedule}>
                    {INVOICE_SCHEDULE_LABELS[schedule]}
                  </option>
                ))}
              </Select>
              <p className="mt-1 text-xs text-ink-muted">{SCHEDULE_HINT[state.schedule]}</p>
            </div>

            {state.next ? (
              <p className="text-xs">
                Næste kladde {formatDate(state.next.run_date)} for{' '}
                {formatDate(state.next.period.start)} – {formatDate(state.next.period.end)}.
                {state.set_by_name ? (
                  <span className="block text-ink-muted">
                    Sat af {state.set_by_name}. Kladden oprettes i deres navn i revisionssporet.
                  </span>
                ) : null}
              </p>
            ) : null}

            {state.schedule !== 'manual' && !state.job_enabled ? (
              <Alert tone="warning">
                Den automatiske fakturering kører ikke på denne server. Planen er gemt og bruges af
                produktionen.
              </Alert>
            ) : null}

            {state.last_run ? <LastRun run={state.last_run} /> : null}

            {error ? <Alert tone="error">{error}</Alert> : null}

            <p className="text-xs text-ink-muted">
              Kladden har samme grundlag som /fakturering/ny med kun kunden valgt: alle vundne
              aftaler og timerne uden aftale, klar til fakturering op til periodens slutdato, og
              bufferen regnet af aftalernes samlede buffer. Timer, der lå klar, før planen blev
              slået til, kommer ikke med — dem fakturerer du på /fakturering/ny.
            </p>
            <p className="text-xs text-ink-muted">
              Kladden, der lukker en måned, tager også abonnementerne for den kommende måned med:
              applikationsdrift og apps på kundens vundne aftaler i CRM, til varens pris i Business
              Central, og kundens abonnementer på{' '}
              <PermittedLink
                to="/fakturering/abonnementer"
                allowed={can('subscriptions.view')}
                className="underline"
              >
                Abonnementsoversigt
              </PermittedLink>
              , når en periode begynder. Har kunden ingen timer, laves kladden alligevel — med
              abonnementerne alene.
            </p>
          </>
        )}
      </CardBody>
    </Card>
  );
}

function LastRun({ run }: { run: InvoiceScheduleRun }) {
  const { user } = useAuth();
  const tone = run.outcome === 'failed' ? 'error' : run.outcome === 'skipped' ? 'warning' : null;
  const text = (
    <>
      <span className="font-medium">{RUN_OUTCOME_TEXT[run.outcome]}</span>
      {run.invoice_no ? (
        <>
          {' '}
          — {/* Made as a draft, but it may have been posted since - so the status is not known. */}
          <PermittedLink
            to={`/fakturering/${encodeURIComponent(run.invoice_no)}`}
            allowed={canOpenInvoice(user, null)}
            className="hover:underline"
          >
            {run.invoice_no}
          </PermittedLink>
        </>
      ) : null}{' '}
      <span className="text-ink-muted">
        ({formatDate(run.period.start)} – {formatDate(run.period.end)}, {formatDateTime(run.at)})
      </span>
      {run.message ? <span className="mt-1 block">{run.message}</span> : null}
    </>
  );

  return tone ? (
    <Alert tone={tone}>{text}</Alert>
  ) : (
    <p className="text-xs">Seneste kørsel: {text}</p>
  );
}
